"use client";

import { motion } from "src/lib/motion";
import { Check } from "lucide-react";
import Section from "@/components/section";
import Image from "next/image";
import { useTranslations } from "@/lib/hooks/use-translations";

// Import Bilder
import roadmapImage from "@/assets/images/roadmap.svg";
import dataImage from "@/assets/images/data.svg";
import workflowImage from "@/assets/images/workflow.svg";
import trainingImage from "@/assets/images/training.svg";

interface ServiceProps {
  title: string;
  description: string;
  points: string[];
  image: string;
  url: string;
  isReversed?: boolean;
}

interface ServicesSectionProps {
  locale: string;
}

function Service({ title, description, points, image, url, isReversed = false }: ServiceProps) {
  return (
    <div className={`grid md:grid-cols-2 gap-4 md:gap-12 items-center max-md:pb-4 max-md:!mt-16`}>
      <div className={`space-y-6 max-md:row-start-2 ${isReversed ? 'md:col-start-2' : ''}`}>
        <motion.h4
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-3xl text-center mt-2 lg:text-left break-words"
        >
          {title}
        </motion.h4>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-muted-foreground max-md:text-center"
        >
          {description}
        </motion.p>
        <motion.ul
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="space-y-3"
        >
          {points.map((point, index) => (
            <li key={index} className="flex items-center gap-3">
              <div className="min-h-6 min-w-6 max-h-6 max-w-6 rounded-full bg-primary/10 flex items-center justify-center">
                <Check className="h-4 w-4 text-primary" />
              </div>
              <span>{point}</span>
            </li>
          ))}
        </motion.ul>
      </div>
      <motion.div
        initial={{ opacity: 0, x: isReversed ? -20 : 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        viewport={{ once: true }}
        className={`relative ${isReversed ? 'md:col-start-1 md:row-start-1' : ''}`}
      >
        <div className={`w-full h-auto md:h-[400px] rounded-lg overflow-hidden relative z-10 ${image.endsWith('.svg') ? 'bg-neutral-100 dark:bg-neutral-900' : ''}`}>
          <Image
            src={image}
            alt={title}
            className={`w-full h-full ${image.endsWith('.svg') ? 'object-contain mix-blend-multiply dark:mix-blend-screen' : 'object-cover'}`}
            width={800}
            height={600}
          />
        </div>
        <div className="absolute inset-0 rounded-lg bg-gradient-to-tr from-primary/20 to-transparent opacity-60"></div>
      </motion.div>
    </div>
  );
}

export default function ServicesSection({ locale }: ServicesSectionProps) {
  const { t } = useTranslations();

  // Image mapping for services
  const imageMap = [
    roadmapImage.src,
    dataImage.src,
    workflowImage.src,
    trainingImage.src
  ];

  // URL mapping based on locale
  const getServiceUrl = (index: number) => {
    const paths = ['strategy', 'infrastructure', 'implementation', 'training'];
    const baseUrl = 'https://savagescorp.ai';
    const localePrefix = locale === 'de' ? '/de/solutions/ki-beratung' : locale === 'zh-CN' ? '/zh-CN/solutions/ai-consulting' : '/solutions/ai-consulting';
    return `${baseUrl}${localePrefix}/${paths[index]}`;
  };

  // Get services from translations
  const servicesData = t('pages.about.services.items') as Array<{
    title: string;
    description: string;
    points: string[];
  }>;

  const services = servicesData.map((service, index) => ({
    ...service,
    image: imageMap[index],
    url: getServiceUrl(index)
  }));

  return (
    <Section
      title={t('pages.about.services.title')}
      subtitle={t('pages.about.services.subtitle')}
      className="bg-neutral-100 dark:bg-neutral-900"
      titleBlockClassName="max-md:pb-2 pb-16"
    >
      <div className="space-y-20 max-w-5xl mx-auto">
        {services.map((service, index) => (
          <Service
            key={index}
            title={service.title}
            description={service.description}
            points={service.points}
            image={service.image}
            url={service.url}
            isReversed={index % 2 !== 0}
          />
        ))}
      </div>
    </Section>
  );
}
